import { useCallback, useId, useRef, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { DialogCloseButton } from './DialogCloseButton.js';
import styles from './DetailDrawer.module.css';

export type DetailDrawerVariant = 'drawer' | 'sheet';
/** `end` = side inspect drawer; sheet placements mirror chrome hosts. */
export type DetailDrawerPlacement = 'end' | 'bottom' | 'top' | 'topEnd';
export type DetailDrawerCloseGlyph = 'arrowLeft' | 'x';
export type DetailDrawerSurface = 'default' | 'elevated';

export interface DetailDrawerProps {
  readonly open: boolean;
  readonly title: string;
  /** Supporting copy under the title; wires `aria-describedby` when set. */
  readonly subtitle?: string;
  /**
   * Optional description for `aria-describedby`.
   * When set, takes precedence over `subtitle` for describedby + visible copy.
   */
  readonly description?: string;
  readonly size?: 'default' | 'wide';
  /**
   * `drawer` = side inspect (default). `sheet` = chrome bottom/top sheets (LOCK A).
   */
  readonly variant?: DetailDrawerVariant;
  /**
   * Layout placement. Defaults: drawer → `end`; sheet → `bottom`.
   */
  readonly placement?: DetailDrawerPlacement;
  /**
   * Close control glyph. Defaults: drawer → `arrowLeft`; sheet → `x` (DialogCloseButton).
   */
  readonly closeGlyph?: DetailDrawerCloseGlyph;
  /** Panel surface token. More chrome uses `elevated`. */
  readonly surface?: DetailDrawerSurface;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  /** When true, Escape and backdrop dismiss are blocked (busy-dismiss matrix). */
  readonly isBusy?: boolean;
  /** Overrides default `detail-drawer` content test id. */
  readonly contentTestId?: string;
  /** Overrides default `detail-drawer-overlay` overlay test id. */
  readonly overlayTestId?: string;
  /** Overrides default close control test id (`detail-drawer-back` / `dialog-close`). */
  readonly closeTestId?: string;
  /** Optional DOM id on content (e.g. `aria-controls` target for BN More). */
  readonly contentId?: string;
  /** When set, writes `data-export-exclude` on the overlay (chrome sheets). */
  readonly exportExclude?: string;
  /** Extra data-* attributes on content (host accordion thresholds, etc.). */
  readonly contentDataAttrs?: Readonly<Record<string, string | undefined>>;
  readonly contentClassName?: string;
}

function resolvePlacement(
  variant: DetailDrawerVariant,
  placement: DetailDrawerPlacement | undefined,
): DetailDrawerPlacement {
  if (placement !== undefined) {
    return placement;
  }
  return variant === 'sheet' ? 'bottom' : 'end';
}

function resolveCloseGlyph(
  variant: DetailDrawerVariant,
  closeGlyph: DetailDrawerCloseGlyph | undefined,
): DetailDrawerCloseGlyph {
  if (closeGlyph !== undefined) {
    return closeGlyph;
  }
  return variant === 'sheet' ? 'x' : 'arrowLeft';
}

function placementClass(placement: DetailDrawerPlacement): string {
  switch (placement) {
    case 'bottom':
      return styles.placementBottom ?? '';
    case 'top':
      return styles.placementTop ?? '';
    case 'topEnd':
      return styles.placementTopEnd ?? '';
    case 'end':
    default:
      return styles.placementEnd ?? '';
  }
}

export function DetailDrawer({
  open,
  title,
  subtitle,
  description,
  size = 'default',
  variant = 'drawer',
  placement: placementProp,
  closeGlyph: closeGlyphProp,
  surface = 'default',
  onClose,
  children,
  footer,
  isBusy = false,
  contentTestId = 'detail-drawer',
  overlayTestId = 'detail-drawer-overlay',
  closeTestId,
  contentId,
  exportExclude,
  contentDataAttrs,
  contentClassName,
}: DetailDrawerProps): JSX.Element {
  const { t } = useTranslation('admin');
  const reactId = useId();
  const titleId = `detail-drawer-title-${reactId}`;
  const descriptionId = `detail-drawer-description-${reactId}`;
  const closeRef = useRef<HTMLButtonElement>(null);
  const backLabel = t('common.back', { defaultValue: 'Back' });
  const closeAria = t('usage.common.i18nHardening.closeDrawerAria');

  const placement = resolvePlacement(variant, placementProp);
  const closeGlyph = resolveCloseGlyph(variant, closeGlyphProp);
  const resolvedCloseTestId =
    closeTestId ?? (closeGlyph === 'x' ? 'dialog-close' : 'detail-drawer-back');

  const describedText = description ?? subtitle;
  const hasDescription =
    typeof describedText === 'string' && describedText.trim().length > 0;

  const handleOpenChange = useCallback((nextOpen: boolean): void => {
    if (!nextOpen && !isBusy) {
      onClose();
    }
  }, [isBusy, onClose]);

  const handleInteractOutside = useCallback((event: Event): void => {
    if (isBusy) {
      event.preventDefault();
    }
  }, [isBusy]);

  const handleEscapeKeyDown = useCallback((event: KeyboardEvent): void => {
    if (isBusy) {
      event.preventDefault();
    }
  }, [isBusy]);

  const handleOpenAutoFocus = useCallback((event: Event): void => {
    event.preventDefault();
    closeRef.current?.focus();
  }, []);

  const contentClassNames = [
    styles.content,
    variant === 'sheet' ? styles.variantSheet : styles.variantDrawer,
    placementClass(placement),
    size === 'wide' ? styles.sizeWide : styles.sizeDefault,
    surface === 'elevated' ? styles.surfaceElevated : null,
    contentClassName,
  ]
    .filter(Boolean)
    .join(' ');

  const dataAttrEntries = contentDataAttrs
    ? Object.entries(contentDataAttrs).filter(
        (entry): entry is [string, string] => typeof entry[1] === 'string',
      )
    : [];

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={styles.overlay}
          data-testid={overlayTestId}
          data-export-exclude={exportExclude}
        />
        <Dialog.Content
          id={contentId}
          className={contentClassNames}
          aria-labelledby={titleId}
          aria-describedby={hasDescription ? descriptionId : undefined}
          aria-busy={isBusy || undefined}
          data-testid={contentTestId}
          data-drawer-variant={variant}
          data-drawer-placement={placement}
          data-drawer-size={size}
          data-drawer-busy={isBusy ? 'true' : undefined}
          data-drawer-surface={surface}
          onPointerDownOutside={handleInteractOutside}
          onInteractOutside={handleInteractOutside}
          onEscapeKeyDown={handleEscapeKeyDown}
          onOpenAutoFocus={handleOpenAutoFocus}
          {...Object.fromEntries(dataAttrEntries)}
        >
          <header
            className={
              closeGlyph === 'x' ? styles.headerSheet : styles.header
            }
          >
            {closeGlyph === 'arrowLeft' ? (
              <Dialog.Close asChild>
                <button
                  ref={closeRef}
                  type="button"
                  className={styles.backButton}
                  aria-label={closeAria}
                  data-testid={resolvedCloseTestId}
                  disabled={isBusy}
                >
                  <ArrowLeft className={styles.backIcon} aria-hidden />
                  <span className="max-sm:sr-only">{backLabel}</span>
                </button>
              </Dialog.Close>
            ) : null}
            <div className={styles.titleBlock}>
              <Dialog.Title asChild>
                <h2 id={titleId} className={styles.title}>
                  {title}
                </h2>
              </Dialog.Title>
              {hasDescription ? (
                <Dialog.Description id={descriptionId} className={styles.subtitle}>
                  {describedText}
                </Dialog.Description>
              ) : null}
            </div>
            {closeGlyph === 'x' ? (
              <Dialog.Close asChild>
                <DialogCloseButton
                  ref={closeRef}
                  testId={resolvedCloseTestId}
                  disabled={isBusy}
                />
              </Dialog.Close>
            ) : null}
          </header>
          <div className={`admin-scrollbar rp-drawer-body ${styles.body}`}>{children}</div>
          {footer ? (
            <footer className={styles.footer} data-testid="detail-drawer-footer">
              {footer}
            </footer>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Thin alias of DetailDrawer sheet API — never a fifth D5 overlay primitive. */
export const AppSheet = DetailDrawer;
export type AppSheetProps = DetailDrawerProps;
